import type { VoiceEndReason, VoiceServerMessage, WidgetMessage } from './types';

/**
 * The widget side of a voice call (API: VOICE_PATH in @vixsync/shared). Microphone audio is
 * resampled to 16 kHz 16-bit PCM and sent as binary frames; the assistant's 24 kHz PCM is
 * queued for gapless playback and dropped at once when the visitor talks over it.
 */
const INPUT_RATE = 16_000;
const OUTPUT_RATE = 24_000;
/** Samples per microphone callback (≈ 20–25 ms at 44.1/48 kHz, as Google recommends). */
const MIC_BUFFER = 1024;
/** Opus (VOICE_CODECS in @vixsync/shared): ~24 kbps each way instead of 256/384 kbps PCM. */
const OPUS_BITRATE = 24_000;
const OPUS_FRAME_US = 20_000;
/** Playback head start; grows (up to the max) when audio arrives late on a slow connection. */
const MIN_LEAD = 0.04;
const MAX_LEAD = 0.3;
/** Microphone audio not yet sent beyond this (a stalled upload) is dropped, not queued. */
const MAX_UNSENT_BYTES = 16_000;

/** Whether this browser can encode and decode Opus with WebCodecs (most current browsers). */
export async function opusSupported(): Promise<boolean> {
  if (typeof AudioEncoder === 'undefined' || typeof AudioDecoder === 'undefined') return false;
  try {
    const [enc, dec] = await Promise.all([
      AudioEncoder.isConfigSupported({
        codec: 'opus',
        sampleRate: INPUT_RATE,
        numberOfChannels: 1,
        bitrate: OPUS_BITRATE,
      }),
      AudioDecoder.isConfigSupported({
        codec: 'opus',
        sampleRate: OUTPUT_RATE,
        numberOfChannels: 1,
      }),
    ]);
    return Boolean(enc.supported && dec.supported);
  } catch {
    return false;
  }
}

/** "thinking": the assistant is looking something up. */
export type VoiceState = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'ended';

export interface VoiceHandlers {
  onState(state: VoiceState): void;
  onReady(maxSeconds: number): void;
  onTranscript(role: 'user' | 'ai', text: string): void;
  onMessage(message: WidgetMessage): void;
  onEnded(reason: VoiceEndReason, message: string): void;
  /** Microphone loudness, 0–1, for the animation. */
  onLevel(level: number): void;
}

/** Chrome's `document.featurePolicy` / `permissionsPolicy` (not in every browser or in lib.dom). */
interface FeaturePolicy {
  allowsFeature(feature: string): boolean;
}

type AudioContextCtor = typeof AudioContext;
const audioContextCtor = (): AudioContextCtor | undefined =>
  typeof window === 'undefined'
    ? undefined
    : (window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext);

/**
 * Voice needs a microphone API, Web Audio and WebSockets (all modern browsers, over https), and
 * a page that allows the microphone: a `Permissions-Policy: microphone=()` header blocks it.
 */
export function voiceSupported(): boolean {
  const policy =
    typeof document === 'undefined'
      ? undefined
      : ((document as { permissionsPolicy?: FeaturePolicy; featurePolicy?: FeaturePolicy })
          .permissionsPolicy ?? (document as { featurePolicy?: FeaturePolicy }).featurePolicy);
  if (policy && !policy.allowsFeature('microphone')) return false;
  return Boolean(
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    audioContextCtor() &&
    typeof WebSocket !== 'undefined',
  );
}

/** Averages the device-rate microphone signal down to 16 kHz, carrying leftovers between calls. */
export class Downsampler {
  private rest = new Float32Array(0);
  private pos = 0;

  constructor(private readonly ratio: number) {}

  push(chunk: Float32Array): Int16Array<ArrayBuffer> {
    const input = new Float32Array(this.rest.length + chunk.length);
    input.set(this.rest);
    input.set(chunk, this.rest.length);
    const out: number[] = [];
    let pos = this.pos;
    while (pos + this.ratio <= input.length) {
      const start = Math.floor(pos);
      const end = Math.max(start + 1, Math.floor(pos + this.ratio));
      let sum = 0;
      for (let i = start; i < end; i++) sum += input[i]!;
      out.push(sum / (end - start));
      pos += this.ratio;
    }
    const keep = Math.floor(pos);
    this.rest = input.slice(keep);
    this.pos = pos - keep;
    const pcm = new Int16Array(out.length);
    for (let i = 0; i < out.length; i++) pcm[i] = Math.max(-1, Math.min(1, out[i]!)) * 0x7fff;
    return pcm;
  }
}

export class VoiceClient {
  private ws?: WebSocket;
  private ctx?: AudioContext;
  private stream?: MediaStream;
  private mic?: ScriptProcessorNode;
  private source?: MediaStreamAudioSourceNode;
  private readonly playing = new Set<AudioBufferSourceNode>();
  private playAt = 0;
  private lead = MIN_LEAD;
  private muted = false;
  /** Opus in both directions, once the API agrees to it. */
  private encoder?: AudioEncoder;
  private decoder?: AudioDecoder;
  private micTime = 0;
  private packetTime = 0;
  /** Decoded audio from before an interruption (timestamps below this) is not played. */
  private dropBefore = 0;
  /** A knowledge search is running; stays "thinking" until the answer starts playing. */
  private searching = false;
  private ended = false;
  private state: VoiceState = 'connecting';

  constructor(
    private readonly apiUrl: string,
    private readonly token: string,
    private readonly handlers: VoiceHandlers,
    /** Talk in a new conversation (after "New chat"). */
    private readonly newConversation = false,
  ) {}

  private setState(state: VoiceState) {
    if (this.state === state || this.ended) return;
    this.state = state;
    this.handlers.onState(state);
  }

  /** Call from a click: browsers only allow audio and the microphone after a user gesture. */
  async start() {
    const Ctor = audioContextCtor()!;
    this.ctx = new Ctor();
    void this.ctx.resume();
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      this.finish(
        'error',
        'Please allow microphone access to talk, or type your question instead.',
      );
      return;
    }
    if (this.ended) return this.cleanup();
    const opus = await opusSupported();
    if (this.ended) return this.cleanup();

    const url = `${this.apiUrl.replace(/\/+$/, '').replace(/^http/, 'ws')}/api/v1/widget/voice`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () =>
      ws.send(
        JSON.stringify({
          type: 'start',
          token: this.token,
          ...(this.newConversation ? { newConversation: true } : {}),
          ...(opus ? { codec: 'opus' } : {}),
        }),
      );
    ws.onmessage = (e: MessageEvent) => {
      if (e.data instanceof ArrayBuffer) this.play(e.data);
      else this.onServer(JSON.parse(String(e.data)) as VoiceServerMessage);
    };
    ws.onclose = () =>
      this.finish('error', 'The call was disconnected. You can keep chatting by typing.');
  }

  private onServer(msg: VoiceServerMessage) {
    switch (msg.type) {
      case 'ready':
        if (msg.codec === 'opus') this.startOpus();
        this.listen();
        this.chime(true);
        this.handlers.onReady(msg.maxSeconds);
        return this.setState('listening');
      case 'searching':
        this.searching = true;
        if (!this.playing.size) this.setState('thinking');
        return;
      case 'transcript':
        return this.handlers.onTranscript(msg.role, msg.text);
      case 'interrupted':
        return this.flush();
      case 'message':
        return this.handlers.onMessage(msg.message);
      case 'ended':
        return this.finish(msg.reason, msg.message);
      case 'turn_complete':
        return;
    }
  }

  /** Starts streaming the microphone. */
  private listen() {
    const ctx = this.ctx!;
    const down = new Downsampler(ctx.sampleRate / INPUT_RATE);
    this.source = ctx.createMediaStreamSource(this.stream!);
    this.mic = ctx.createScriptProcessor(MIC_BUFFER, 1, 1);
    this.mic.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      let sum = 0;
      for (let i = 0; i < input.length; i += 4) sum += input[i]! * input[i]!;
      this.handlers.onLevel(this.muted ? 0 : Math.min(1, Math.sqrt(sum / (input.length / 4)) * 4));
      if (this.muted || this.ws?.readyState !== WebSocket.OPEN) return;
      const pcm = down.push(input);
      if (!pcm.length) return;
      // On a stalled upload, old speech is dropped rather than arriving seconds late.
      if (this.ws.bufferedAmount > MAX_UNSENT_BYTES) return;
      if (!this.encoder) return this.ws.send(pcm);
      const data = new AudioData({
        format: 's16',
        sampleRate: INPUT_RATE,
        numberOfFrames: pcm.length,
        numberOfChannels: 1,
        timestamp: this.micTime,
        data: pcm,
      });
      this.micTime += (pcm.length / INPUT_RATE) * 1e6;
      this.encoder.encode(data);
      data.close();
    };
    this.source.connect(this.mic);
    // Chrome only runs the processor when it is connected to the output (it outputs silence).
    this.mic.connect(ctx.destination);
  }

  /**
   * Opus both ways: the browser's own encoder and decoder (WebCodecs), so the widget stays small.
   * Any codec error ends Opus for this call's playback but the call goes on.
   */
  private startOpus() {
    const ws = this.ws;
    this.encoder = new AudioEncoder({
      output: (chunk) => {
        if (ws?.readyState !== WebSocket.OPEN) return;
        const packet = new Uint8Array(chunk.byteLength);
        chunk.copyTo(packet);
        ws.send(packet);
      },
      error: () => undefined,
    });
    this.encoder.configure({
      codec: 'opus',
      sampleRate: INPUT_RATE,
      numberOfChannels: 1,
      bitrate: OPUS_BITRATE,
      opus: { frameDuration: OPUS_FRAME_US },
    });
    this.decoder = new AudioDecoder({
      output: (audio) => {
        if (audio.timestamp >= this.dropBefore) {
          const samples = new Float32Array(audio.numberOfFrames);
          audio.copyTo(samples, { planeIndex: 0, format: 'f32-planar' });
          this.schedule(samples, audio.sampleRate);
        }
        audio.close();
      },
      error: () => undefined,
    });
    this.decoder.configure({ codec: 'opus', sampleRate: OUTPUT_RATE, numberOfChannels: 1 });
  }

  /** The assistant's audio from the API: an Opus packet, or 24 kHz PCM. */
  private play(data: ArrayBuffer) {
    if (!this.ctx || this.ended || data.byteLength < 1) return;
    if (this.decoder) {
      if (this.decoder.state !== 'configured') return;
      this.decoder.decode(new EncodedAudioChunk({ type: 'key', timestamp: this.packetTime, data }));
      this.packetTime += OPUS_FRAME_US;
      return;
    }
    if (data.byteLength < 2) return;
    const pcm = new Int16Array(data, 0, data.byteLength >> 1);
    const samples = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) samples[i] = pcm[i]! / 0x8000;
    this.schedule(samples, OUTPUT_RATE);
  }

  /** Queues audio right after what is already playing. */
  private schedule(samples: Float32Array, rate: number) {
    const ctx = this.ctx;
    if (!ctx || this.ended || !samples.length) return;
    const buffer = ctx.createBuffer(1, samples.length, rate);
    buffer.getChannelData(0).set(samples);
    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(ctx.destination);
    const now = ctx.currentTime;
    // The queue ran dry mid-answer (audio arrived late): keep a bigger head start from now on.
    if (this.playAt && this.playAt < now && now - this.playAt < 1) {
      this.lead = Math.min(MAX_LEAD, this.lead + 0.06);
    }
    this.playAt = Math.max(this.playAt, now + this.lead);
    node.start(this.playAt);
    this.playAt += buffer.duration;
    this.playing.add(node);
    this.setState('speaking');
    this.searching = false;
    node.onended = () => {
      this.playing.delete(node);
      if (!this.playing.size) this.setState(this.searching ? 'thinking' : 'listening');
    };
  }

  /** Stops the assistant mid-sentence (the visitor started talking). */
  private flush() {
    for (const node of this.playing) {
      node.onended = null;
      try {
        node.stop();
      } catch {
        // Already stopped.
      }
    }
    this.playing.clear();
    this.playAt = 0;
    // Packets already in the decoder belong to the interrupted answer.
    this.dropBefore = this.packetTime;
    this.setState('listening');
  }

  setMuted(muted: boolean) {
    this.muted = muted;
  }

  /** The visitor filled in the details form during the call; false when the call is gone. */
  sendDetails(details: { name: string; phone: string; email: string }): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify({ type: 'details', ...details }));
    return true;
  }

  /** The visitor hung up. */
  stop() {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'end' }));
    this.finish('completed', 'Call ended.');
  }

  /** A soft two-note chime: rising when the call connects, falling when it ends. */
  private chime(up: boolean) {
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'closed') return;
    const at = Math.max(ctx.currentTime, this.playAt);
    (up ? [660, 880] : [660, 494]).forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      const start = at + i * 0.12;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.06, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.24);
    });
    this.playAt = Math.max(this.playAt, at + 0.36);
  }

  private finish(reason: VoiceEndReason, message: string) {
    if (this.ended) return;
    if (this.mic) this.chime(false);
    this.ended = true;
    this.state = 'ended';
    this.handlers.onState('ended');
    this.handlers.onEnded(reason, message);
    if (this.ws) {
      this.ws.onclose = null;
      if (this.ws.readyState <= WebSocket.OPEN) this.ws.close();
    }
    // Let the last words finish before releasing the audio device.
    const left = this.ctx ? Math.max(0, this.playAt - this.ctx.currentTime) : 0;
    setTimeout(() => this.cleanup(), Math.min(4000, left * 1000));
  }

  private cleanup() {
    this.mic?.disconnect();
    this.source?.disconnect();
    if (this.mic) this.mic.onaudioprocess = null;
    for (const codec of [this.encoder, this.decoder]) {
      if (codec && codec.state !== 'closed') codec.close();
    }
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    void this.ctx?.close().catch(() => undefined);
  }
}
